import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import GameCard from "../components/games/GameCard";
import RecommendationFeedback from "../components/games/RecommendationFeedback";
import SearchBar from "../components/SearchBarC";
import Button from "../components/ui/Button";
import {
  searchGames,
  getRandomGames,
  getPopularGames,
  getRecentGames,
  getClassicGames,
} from "../api/games";
import {
  advancedPersonalizedSearch,
  getPersonalizedRecommendationsV2,
} from "../api/search";
import { releaseWindowToRange } from "../utils/releaseWindow";

const GameCardSkeleton = () => (
  <div className="rounded-3xl border border-white/10 bg-surface-card/60 backdrop-blur-md shadow-card overflow-hidden">
    <div className="h-[210px] gg-shimmer" />
    <div className="p-4 space-y-2.5">
      <div className="h-6 rounded gg-shimmer" />
      <div className="h-4 w-2/3 rounded gg-shimmer" />
      <div className="flex gap-2 mt-2">
        <div className="h-6 w-14 rounded-full gg-shimmer" />
        <div className="h-6 w-20 rounded-full gg-shimmer" />
      </div>
    </div>
  </div>
);

const gridClass =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 w-full max-w-[1400px] mx-auto my-4";

export const HomePage = () => {
  const { blacklist = [], user } = useAuth();

  // Inject Poppins and Aclonica fonts from Google Fonts
  useEffect(() => {
    const poppinsLink = document.createElement("link");
    poppinsLink.href =
      "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap";
    poppinsLink.rel = "stylesheet";
    document.head.appendChild(poppinsLink);

    const aclonicaLink = document.createElement("link");
    aclonicaLink.href =
      "https://fonts.googleapis.com/css2?family=Aclonica&display=swap";
    aclonicaLink.rel = "stylesheet";
    document.head.appendChild(aclonicaLink);

    return () => {
      document.head.removeChild(poppinsLink);
      document.head.removeChild(aclonicaLink);
    };
  }, []);

  const [games, setGames] = useState([]);
  const [filters] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentView, setCurrentView] = useState("empty");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [error, setError] = useState(null);

  // New state for personalized recommendations
  const [personalizedRecommendations, setPersonalizedRecommendations] =
    useState([]);
  const [recommendationCategory] = useState("all");
  const [recommendationOffset, setRecommendationOffset] = useState(0);
  const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [totalRecommendations, setTotalRecommendations] = useState(0);

  // Search results state
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSearchParams, setCurrentSearchParams] = useState(null);
  const [refreshCount] = useState(0);
  const resultsRef = useRef(null);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(false);
        setGames([]);
        setPagination({ page: 1, totalPages: 1, total: 0 });

        // If user is logged in, load personalized recommendations
        if (user) {
          await loadPersonalizedRecommendations();
        } else {
          setCurrentView("empty");
        }
        setError(null);
      } catch (err) {
        console.error("💥 Error in loadInitialData:", err);
        setError("Failed to initialize homepage. Please try again.");
        setGames([]);
        setLoading(false);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load personalized recommendations for logged-in users
  const loadPersonalizedRecommendations = useCallback(
    async (offset = 0) => {
      if (!user) return;

      try {
        setLoadingRecommendations(true);
        const result = await getPersonalizedRecommendationsV2(
          20,
          offset,
          0,
          recommendationCategory,
        );

        if (result && result.recommendations) {
          if (offset === 0) {
            setPersonalizedRecommendations(result.recommendations);
          } else {
            setPersonalizedRecommendations((prev) => [
              ...prev,
              ...result.recommendations,
            ]);
          }
          setTotalRecommendations(
            result.total || result.recommendations.length,
          );
          setHasMoreRecommendations(result.hasMore);
          setRecommendationOffset(offset + result.recommendations.length);
          setCurrentView("recommendations");
        }
      } catch (error) {
        console.error("Error loading personalized recommendations:", error);
      } finally {
        setLoadingRecommendations(false);
      }
    },
    [user, recommendationCategory],
  );

  const handleSearch = useCallback(
    async (searchParams, isRefresh = false) => {
      try {
        setSearchLoading(true);
        try {
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } catch (e) {}
        setError(null);

        const currentPage = isRefresh ? searchPage + 1 : 1;

        if (!isRefresh) {
          setCurrentSearchParams(searchParams);
          setSearchPage(1);
        }

        const cleanParams = {
          query: String(searchParams.query || "").trim(),
          genre: String(searchParams.genre || "").trim(),
          minScore: String(searchParams.minScore || "").trim(),
          platform: String(searchParams.platform || "").trim(),
          tags: Array.isArray(searchParams.tags)
            ? searchParams.tags
            : searchParams.tags
              ? [searchParams.tags]
              : [],
          releaseWindow: String(searchParams.releaseWindow || "").trim(),
          gameMode: String(searchParams.gameMode || "").trim(),
          quickFilter: searchParams.quickFilter || "",
        };

        setSearchQuery(cleanParams.query);
        setCurrentView("search");

        if (cleanParams.quickFilter && !isRefresh) {
          let result;
          switch (cleanParams.quickFilter) {
            case "Popular":
              result = await getPopularGames(currentPage, 5, blacklist);
              setCurrentView("popular");
              break;
            case "Recent Releases":
              result = await getRecentGames(currentPage, 5, blacklist);
              setCurrentView("recent");
              break;
            case "Classics":
              result = await getClassicGames(currentPage, 5, blacklist);
              setCurrentView("classic");
              break;
            case "Surprise me":
              result = await getRandomGames(5, {}, blacklist);
              setCurrentView("random");
              setSearchResults(result.games || result || []);
              setHasMoreResults(false);
              setSearchLoading(false);
              return;
            default:
              result = {
                games: [],
                pagination: { page: 1, totalPages: 1, total: 0 },
              };
          }

          if (isRefresh) {
            setSearchResults((prev) => [...prev, ...(result.games || [])]);
            setSearchPage(currentPage);
          } else {
            setSearchResults(result.games || []);
            setSearchPage(1);
          }

          setHasMoreResults((result.games || []).length === 5);
          setSearchLoading(false);
          try {
            resultsRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          } catch (e) {}
          return;
        }

        const apiFilters = {};
        if (cleanParams.query) apiFilters.query = cleanParams.query;
        if (cleanParams.genre) apiFilters.genres = [cleanParams.genre];
        if (cleanParams.platform) apiFilters.platforms = [cleanParams.platform];
        if (cleanParams.tags && cleanParams.tags.length > 0)
          apiFilters.tags = cleanParams.tags;
        if (cleanParams.minScore)
          apiFilters.minCriticScore = parseInt(cleanParams.minScore);
        if (cleanParams.releaseWindow) {
          const { releaseDateFrom, releaseDateTo } = releaseWindowToRange(
            cleanParams.releaseWindow,
          );
          if (releaseDateFrom) apiFilters.releaseDateFrom = releaseDateFrom;
          if (releaseDateTo) apiFilters.releaseDateTo = releaseDateTo;
        }
        if (cleanParams.gameMode) apiFilters.gameMode = cleanParams.gameMode;

        let result;

        if (user) {
          // For refresh, send a new seed to get different results
          const refreshSeed = isRefresh ? Date.now() : null;
          result = await advancedPersonalizedSearch(
            apiFilters,
            currentPage,
            5,
            "relevance",
            refreshSeed,
          );
          const newResults = result.games || [];

          if (isRefresh) {
            setSearchResults((prev) => {
              const existingIds = new Set(prev.map((g) => g && g.id));
              const uniqueNew = (newResults || []).filter(
                (g) => g && !existingIds.has(g.id),
              );
              if (uniqueNew.length === 0) {
                setError(
                  "No more unique results available. Try a different search.",
                );
                return prev;
              }
              return [...prev, ...uniqueNew];
            });
            setSearchPage(currentPage);
          } else {
            setSearchResults(newResults);
            setSearchPage(1);
          }

          setHasMoreResults(
            result.hasMore ||
              (newResults.length === 5 && result.total > newResults.length),
          );
        } else {
          // For refresh, send a new seed to get different results
          const refreshSeed = isRefresh ? Date.now() : Date.now();
          result = await searchGames(
            apiFilters,
            currentPage,
            5,
            blacklist,
            refreshSeed,
          );
          const newResults = result.games || [];

          if (isRefresh) {
            setSearchResults((prev) => {
              const existingIds = new Set(prev.map((g) => g && g.id));
              const uniqueNew = (newResults || []).filter(
                (g) => g && !existingIds.has(g.id),
              );
              if (uniqueNew.length === 0) {
                setError(
                  "No more unique results available. Try a different search.",
                );
                return prev;
              }
              return [...prev, ...uniqueNew];
            });
            setSearchPage(currentPage);
          } else {
            setSearchResults(newResults);
            setSearchPage(1);
          }

          setHasMoreResults(
            newResults.length === 5 && result.total > newResults.length,
          );
        }
      } catch (err) {
        console.error("❌ Enhanced search error:", err);
        setError("Search failed. Please try again.");
        if (!isRefresh) {
          setSearchResults([]);
          setGames([]);
        }
      } finally {
        setSearchLoading(false);
      }
    },
    [searchPage, blacklist, user],
  );

  const handleRefreshResults = useCallback(async () => {
    if (!currentSearchParams || searchLoading) return;
    try {
      setSearchLoading(true);
      setError(null);

      // IMPORTANT: Use page 1 with new seed, NOT next page
      const refreshSeed = Date.now();

      let result;
      if (user) {
        result = await advancedPersonalizedSearch(
          currentSearchParams,
          1,
          5,
          "relevance",
          refreshSeed,
        );
      } else {
        result = await searchGames(
          currentSearchParams,
          1,
          5,
          blacklist,
          refreshSeed,
        );
      }

      const newGames = result.games || [];

      if (newGames.length > 0) {
        // Replace existing results with new ones, don't append
        setSearchResults(newGames);
        setSearchPage(1);

        // Check if there are more results available beyond these 5
        setHasMoreResults(
          result.hasMore || (newGames.length === 5 && result.total > 5),
        );

        setError(null);
      } else {
        setHasMoreResults(false);
        setError("No results available. Try a different search.");
      }
    } catch (err) {
      console.error("Refresh error:", err);
      setError("Failed to refresh results. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  }, [currentSearchParams, searchLoading, user, blacklist]);

  const location = useLocation();
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get("search");

    if (searchParam) {
      handleSearch({ query: searchParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const trackVisitedGame = useCallback((game) => {
    console.debug("trackVisitedGame (no-op) for:", game?.id || game);
  }, []);

  const handleCrazierGame = useCallback(
    (crazierGame) => {
      setGames([crazierGame]);
      setSearchResults([crazierGame]);
      setCurrentView("crazier");
      trackVisitedGame(crazierGame);
    },
    [trackVisitedGame],
  );

  const handleLoadMore = async () => {
    if (pagination.page >= pagination.totalPages) return;

    try {
      const nextPage = pagination.page + 1;
      let result;
      if (currentView === "search") {
        if (user) {
          result = await advancedPersonalizedSearch(
            { query: searchQuery, ...filters },
            nextPage,
            25,
            "relevance",
          );
        } else {
          result = await searchGames(
            { query: searchQuery, ...filters },
            nextPage,
            25,
            blacklist,
          );
        }
      } else {
        switch (currentView) {
          case "popular":
            result = await getPopularGames(nextPage, 25, blacklist);
            break;
          case "recent":
            result = await getRecentGames(nextPage, 25, blacklist);
            break;
          case "classic":
            result = await getClassicGames(nextPage, 25, blacklist);
            break;
          default:
            result = await searchGames(
              { page: nextPage },
              nextPage,
              25,
              blacklist,
            );
        }
      }
      setGames((prev) => [...prev, ...(result.games || [])]);
      setPagination(result.pagination || pagination);
    } catch (err) {
      setError("Failed to load more games.");
    }
  };

  const loadMoreRecommendations = async () => {
    if (!hasMoreRecommendations || loadingRecommendations) return;
    await loadPersonalizedRecommendations(recommendationOffset);
  };

  const getViewTitle = () => {
    switch (currentView) {
      case "popular":
        return "Popular Games";
      case "recent":
        return "Recent Releases";
      case "classic":
        return "Classic Games";
      case "random":
        return "Surprise Selection";
      case "crazier":
        return "Crazier Discovery";
      case "search":
        return searchQuery
          ? `Search Results for "${searchQuery}"`
          : "Search Games";
      case "recommendations":
        return user
          ? `Personalized For You (${totalRecommendations} games)`
          : "Trending Games";
      case "empty":
        return "Welcome to Game Goblin";
      default:
        return "Welcome to Game Goblin";
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero — compact decorative panel so the Game Goblin background stays the primary identity */}
      <div className="relative w-full px-4 pt-10 pb-4 sm:pt-14 sm:pb-6">
        <div className="relative mx-auto max-w-2xl overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/20  shadow-card px-6 py-8 sm:px-10 sm:py-7 text-center animate-fadeUp">
          {/* subtle glow accents, contained within the panel */}
          <span className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-40 w-[70%] rounded-full bg-brand-purple/20 blur-3xl" />

          <div className="relative z-10 flex flex-col items-center">
            {/* eyebrow badge */}
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1 text-xs sm:text-sm font-semibold text-brand-gold-soft backdrop-blur-sm mb-5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-gold opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-gold" />
              </span>
              The smart game search engine
            </span>

            <h1
              className="font-display font-extrabold text-white uppercase text-2xl sm:text-2xl md:text-3xl tracking-tight"
              style={{ textShadow: "0 4px 30px rgba(0,0,0,0.5)" }}
            >
              Find your next{" "}
              <span className="gg-gradient-text">favourite game</span>
            </h1>

            <p className="mt-4 max-w-xl text-white/80 text-base sm:text-md font-light font-body">
              Search thousands of titles and get smart, personalised
              recommendations on every game's page, powered by real data.
            </p>

            {/* quick stat chips */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
              {[
                { icon: "🎮", label: "Thousands of games" },
                { icon: "✨", label: "Personalised picks" },
                { icon: "⚡", label: "Instant search" },
              ].map((chip) => (
                <span
                  key={chip.label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 text-sm text-white/85 backdrop-blur-sm"
                >
                  <span>{chip.icon}</span>
                  {chip.label}
                </span>
              ))}
            </div>

            <div className="mt-4 group relative inline-flex">
              <span className="text-white/60 text-sm cursor-help underline decoration-dotted underline-offset-4 hover:text-brand-gold transition-colors">
                How do recommendations work?
              </span>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 rounded-2xl border border-white/10 bg-surface-light/95 backdrop-blur-xl p-3.5 text-xs text-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-[100] shadow-[0_16px_40px_rgba(0,0,0,0.5)]">
                Our recommendations are powered by deep data, a refined scoring
                system, and wildcard suggestions, giving you relevant and
                surprising game picks directly on each game's page.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-[2] px-2 sm:px-4 md:px-6 pb-4 sm:pb-6">
        <SearchBar
          onSearch={handleSearch}
          initialQuery={searchQuery}
          initialFilters={filters}
        />

        {currentView === "empty" && !user && (
          <div className="w-full max-w-[1400px] mx-auto my-8 animate-fadeUp">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-surface-card/50 backdrop-blur-md px-6 py-10 sm:px-10 sm:py-12 text-center">
              <span className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-40 w-[70%] rounded-full bg-brand-purple/25 blur-3xl" />
              <div className="relative">
                <span className="text-4xl">🕹️</span>
                <h4
                  className="mt-3 text-white font-extrabold text-2xl sm:text-3xl text-center font-heading"
                  style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
                >
                  Start your search above
                </h4>
                <p className="mt-2 text-white/60 text-sm sm:text-base max-w-xl mx-auto">
                  Use the search bar and filters to find games by genre, platform,
                  rating and more, or hit <span className="text-brand-gold font-semibold">Surprise Me</span> for something new.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 max-w-[1400px] mx-auto rounded-xl px-4 py-3 border border-red-400/40 bg-red-500/10 backdrop-blur-md text-white flex items-center justify-between gap-3 animate-fadeIn">
            <span className="text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-white/60 hover:text-white"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {!loading && currentView !== "empty" && (
          <div className="flex justify-between items-center w-full max-w-[1400px] mx-auto mb-5 animate-fadeIn">
            <h2
              className="flex items-center gap-3 text-white font-extrabold text-2xl sm:text-3xl font-display"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
            >
              <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
              {getViewTitle()}
            </h2>
            {(pagination.total > 0 || totalRecommendations > 0) && (
              <span className="rounded-full px-4 py-1.5 text-sm font-bold text-brand-gold-soft bg-brand-gold/10 border border-brand-gold/30 backdrop-blur-md">
                {currentView === "recommendations"
                  ? totalRecommendations
                  : pagination.total}{" "}
                games
              </span>
            )}
          </div>
        )}

        <div ref={resultsRef}>
          {loadingRecommendations || searchLoading ? (
            <div className={gridClass}>
              {[...Array(8)].map((_, i) => (
                <GameCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <>
              {/* Personalized Recommendations View */}
              {currentView === "recommendations" &&
                personalizedRecommendations.length > 0 && (
                  <div className="animate-fadeIn">
                    <div className={gridClass}>
                      {personalizedRecommendations.map((game, index) => (
                        <div key={`rec-${game.id}-${index}`} className="h-full">
                          <GameCard
                            game={game}
                            showActions={true}
                            onCrazierGame={handleCrazierGame}
                            onClick={() => trackVisitedGame(game)}
                            source="recommendations"
                            recommendedFrom="personalized"
                          />
                        </div>
                      ))}
                    </div>

                    {hasMoreRecommendations && (
                      <div className="text-center mt-8">
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={loadMoreRecommendations}
                          disabled={loadingRecommendations}
                        >
                          {loadingRecommendations ? "Loading..." : "Load More"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {user && searchResults.length > 0 && (
                    <div className="mt-8 flex justify-center">
                      <div className="rounded-2xl border border-white/10 bg-surface-card/50 backdrop-blur-md px-5 py-3">
                        <RecommendationFeedback
                          key={searchResults.map((g) => g.id).join("-")}
                          filters={currentSearchParams || {}}
                          gameIds={searchResults.map((g) => g.id)}
                          context="search"
                        />
                      </div>
                    </div>
                  )}

              {/* Search Results View */}
              {searchResults.length > 0 && currentSearchParams && (
                <div className="animate-fadeIn">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4 w-full max-w-[1400px] mx-auto">
                    <div>
                      <h3 className="font-heading font-bold text-xl sm:text-2xl bg-gradient-to-r from-brand-gold to-lime-400 bg-clip-text text-transparent">
                        Search Results ({searchResults.length} games)
                      </h3>
                      {refreshCount > 0 && (
                        <p className="text-white/60 text-xs mt-1">
                          Showing expanded results (refresh {refreshCount})
                        </p>
                      )}
                    </div>

                    {hasMoreResults && (
                      <button
                        onClick={handleRefreshResults}
                        disabled={searchLoading}
                        title="Click to load more varied results with progressively broader matching"
                        className={[
                          "flex items-center gap-2 rounded-full border px-5 py-2 text-sm font-semibold transition-all duration-200",
                          searchLoading
                            ? "border-white/20 text-white/30 cursor-not-allowed"
                            : "border-brand-gold text-brand-gold hover:border-lime-400 hover:text-lime-400 hover:bg-lime-400/10 hover:-translate-y-0.5",
                        ].join(" ")}
                      >
                        {searchLoading ? (
                          <span className="h-3.5 w-3.5 rounded-full border-2 border-brand-gold/40 border-t-brand-gold animate-spin" />
                        ) : (
                          <span>↻</span>
                        )}
                        {searchLoading ? "Loading..." : "Refresh for More"}
                      </button>
                    )}
                  </div>

                  <div className={gridClass}>
                    {searchResults.map((game, index) => (
                      <div key={`search-${game.id}-${index}`} className="h-full">
                        <GameCard
                          game={game}
                          showActions={true}
                          onCrazierGame={handleCrazierGame}
                          onClick={() => trackVisitedGame(game)}
                          source="search"
                        />
                      </div>
                    ))}
                  </div>

                  {!hasMoreResults && searchResults.length > 5 && (
                    <p className="text-center mt-6 text-white/60 text-sm">
                      ✨ No more results to load ✨
                    </p>
                  )}

                  {/* Overall thumbs up/down on this result set (logged-in only).
                      Keyed by the shown game ids so it resets for each new search. */}
                  
                </div>
              )}

              {/* Regular Games View (for quick filters) */}
              {games &&
                Array.isArray(games) &&
                games.length > 0 &&
                currentView !== "search" &&
                currentView !== "recommendations" && (
                  <div className="animate-fadeIn">
                    <div className={gridClass}>
                      {games.map((game, index) => (
                        <div key={`${game.id}-${index}`}>
                          <GameCard
                            game={game}
                            showActions={true}
                            onCrazierGame={handleCrazierGame}
                            onClick={() => trackVisitedGame(game)}
                            source={currentView}
                          />
                        </div>
                      ))}
                    </div>

                    {pagination.page < pagination.totalPages &&
                      games.length < (pagination.total || 0) && (
                        <div className="text-center mt-8">
                          <Button variant="primary" size="lg" onClick={handleLoadMore}>
                            More
                          </Button>
                        </div>
                      )}
                  </div>
                )}

              {/* No Results Found */}
              {currentSearchParams &&
                searchResults.length === 0 &&
                !searchLoading && (
                  <div className="text-center py-14 px-4 sm:px-8 animate-fadeIn">
                    <h3 className="text-white/85 font-heading font-bold text-xl mb-2">
                      No Games Found
                    </h3>
                    <p className="text-white/60 max-w-xl mx-auto mb-5 text-sm">
                      We couldn't find any games matching your filters. Try
                      adjusting your search criteria or browse our game
                      collections above.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCurrentSearchParams(null);
                        setSearchResults([]);
                        setSearchQuery("");
                        if (user) {
                          loadPersonalizedRecommendations(0);
                        } else {
                          setCurrentView("empty");
                        }
                      }}
                    >
                      Clear Search
                    </Button>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
